import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan} from 'procedures/utils/powAgnosticFlowchartHelpers';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';
import {assertNever} from 'util/Types';

import {StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  ProcedureRunPlanDetailsViewQuery,
  ProcedureRunPlanDetailsViewQuery_procedureRunPlan_ProcedureRunPlanNode as ProcedureRunPlanNode,
  ProcedureRunPlanDetailsViewQueryVariables,
} from './__generated/ProcedureRunPlanDetailsViewQuery';
import {ProcedureRunStateSummary, ProcedureRunTab} from './MultiProcedureRunOverviewTabContents';
import ProcedureRunPlanDesignTabContents from './ProcedureRunPlanDesignTabContents';
import {
  PROCEDURE_RUN_PLAN_METADATA_TAB_FRAGMENT,
  ProcedureRunPlanMetadataTab,
} from './ProcedureRunPlanMetadataTab';

export const PROCEDURE_RUN_PLAN_DETAILS_VIEW_QUERY: DocumentNode = gql`
  query ProcedureRunPlanDetailsViewQuery($procedureRunPlanId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRunPlan(id: $procedureRunPlanId) {
      ...ProcedureRunPlanMetadataTabGraphQL
      ... on ProcedureRunPlanNode {
        id
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${PROCEDURE_RUN_PLAN_METADATA_TAB_FRAGMENT}
`;

interface ProcedureRunPlanDetailsViewProps {
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
  selectedProcedureRunPlan: ProcedureRunStateSummary;
  currentProcedureRunTab: ProcedureRunTab;
}

export default function ProcedureRunPlanDetailsView({
  studyType,
  studyId,
  procedureType,
  selectedProcedureRunPlan,
  currentProcedureRunTab,
}: ProcedureRunPlanDetailsViewProps): JSX.Element {
  const result = useSkippableBenchlingQuery<
    ProcedureRunPlanDetailsViewQuery,
    ProcedureRunPlanDetailsViewQueryVariables
  >(PROCEDURE_RUN_PLAN_DETAILS_VIEW_QUERY, {
    skip: selectedProcedureRunPlan.id == null,
    variables: {procedureRunPlanId: selectedProcedureRunPlan.id!},
  });

  if (result.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  } else if (result.type === QueryResultType.ERROR || result.data == null) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="An error occurred while loading the procedure run plan data. Refresh the page and try again."
      />
    );
  } else {
    const {data} = result;
    if (isInaccessible(data.procedureRunPlan)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.procedureRunPlan}
          modelType={ModelTypeConstants.PROCEDURE_RUN_PLAN}
          displayErrorText="This procedure run plan is not accessible. Try refreshing the page if this is unexpected."
        />
      );
    }

    const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(
      data.procedureRunPlan
    );
    if (flowchartVersion != null && isInaccessible(flowchartVersion)) {
      return (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Unable to display procedure run plan details. The procedure template or flowchart version is not accessible."
        />
      );
    }

    return (
      <div {...getTestProps({component: 'ProcedureRunPlanDetailsView', element: 'root'})}>
        <RenderProcedureRunPlanDetailsView
          studyType={studyType}
          studyId={studyId}
          procedureType={procedureType}
          procedureRunPlan={data.procedureRunPlan}
          currentProcedureRunTab={currentProcedureRunTab}
          refetch={result.refetch}
        />
      </div>
    );
  }
}

interface RenderProcedureRunPlanDetailsViewProps {
  studyType: StudyType;
  studyId: string;
  procedureType: ProcedureType;
  procedureRunPlan: ProcedureRunPlanNode;
  currentProcedureRunTab: ProcedureRunTab;
  refetch: () => Promise<unknown>;
}

function RenderProcedureRunPlanDetailsView({
  studyType,
  studyId,
  procedureType,
  procedureRunPlan,
  currentProcedureRunTab,
  refetch,
}: RenderProcedureRunPlanDetailsViewProps): JSX.Element {
  switch (currentProcedureRunTab) {
    // Plans don't have execution progress, so OVERVIEW falls through to DESIGN
    case ProcedureRunTab.OVERVIEW:
    case ProcedureRunTab.DESIGN:
      return (
        <ProcedureRunPlanDesignTabContents
          procedureRunPlanId={procedureRunPlan.id}
          studyType={studyType}
          studyId={studyId}
          procedureType={procedureType}
        />
      );
    case ProcedureRunTab.METADATA:
      return <ProcedureRunPlanMetadataTab procedureRunPlan={procedureRunPlan} refetch={refetch} />;
    default:
      assert(false, `Unexpected tab state ${assertNever(currentProcedureRunTab)}`);
      return (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Unexpected tab state. Refresh the page and try again."
        />
      );
  }
}
